export const MAGIC_LINK_CODE_LENGTH = 6;
export const MAGIC_LINK_RESEND_COOLDOWN_MS = 60_000;

export type MagicLinkExpirySource = {
  expiresAt?: string | Date | null;
  expiresInMinutes?: number | null;
};

export type MagicLinkLoginErrorKey =
  | 'emailNotFound'
  | 'userNotActive'
  | 'rateLimited'
  | 'requestFailed'
  | 'networkError';

export type MagicLinkRegisterErrorKey =
  | 'registrationsDisabled'
  | 'emailAlreadyExists'
  | 'usernameAlreadyExists'
  | 'rateLimited'
  | 'requestFailed'
  | 'networkError';

export type MagicLinkVerifyErrorKey =
  | 'incompleteCode'
  | 'invalidCode'
  | 'tooManyAttempts'
  | 'expiredCode'
  | 'codeNotFound'
  | 'emailNotFound'
  | 'userNotActive'
  | 'verificationFailed'
  | 'unknownError';

type HttpLikeError = {
  response?: {
    status?: number;
    data?: {
      errors?: Record<string, string>;
      error?: string;
    };
  };
};

function asHttpLikeError(error: unknown): HttpLikeError | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  return error as HttpLikeError;
}

export function getAuthErrorStatus(error: unknown): number | undefined {
  return asHttpLikeError(error)?.response?.status;
}

export function getAuthErrorFieldErrors(
  error: unknown,
): Record<string, string> | undefined {
  return asHttpLikeError(error)?.response?.data?.errors;
}

export function normalizeMagicLinkCode(code: string): string {
  return code.trim();
}

export function isCompleteMagicLinkCode(code: string): boolean {
  return normalizeMagicLinkCode(code).length === MAGIC_LINK_CODE_LENGTH;
}

/**
 * Parse magic-link request expiry from either absolute or relative fields.
 */
export function parseMagicLinkExpiresAt(
  data: MagicLinkExpirySource,
  nowMs: number = Date.now(),
): Date | null {
  if (data.expiresAt) {
    return data.expiresAt instanceof Date
      ? data.expiresAt
      : new Date(data.expiresAt);
  }
  if (typeof data.expiresInMinutes === 'number') {
    return new Date(nowMs + data.expiresInMinutes * 60_000);
  }
  return null;
}

export function getAuthBusinessErrorCode(error: unknown): string | undefined {
  const code = asHttpLikeError(error)?.response?.data?.error;
  return typeof code === 'string' && code.length > 0 ? code : undefined;
}

export function classifyMagicLinkLoginError(
  error: unknown,
): MagicLinkLoginErrorKey {
  const status = getAuthErrorStatus(error);
  if (status === undefined) {
    return 'networkError';
  }

  if (status === 429) {
    return 'rateLimited';
  }

  if (status === 422) {
    const fieldErrors = getAuthErrorFieldErrors(error);
    if (fieldErrors?.email === 'notFound') {
      return 'emailNotFound';
    }
    if (fieldErrors?.email === 'userNotActive') {
      return 'userNotActive';
    }
  }
  return 'requestFailed';
}

export function classifyMagicLinkRegisterError(
  error: unknown,
): MagicLinkRegisterErrorKey {
  const status = getAuthErrorStatus(error);
  if (status === undefined) {
    return 'networkError';
  }

  if (
    status === 403 &&
    getAuthBusinessErrorCode(error) === 'registrationsDisabled'
  ) {
    return 'registrationsDisabled';
  }

  if (status === 429) {
    return 'rateLimited';
  }

  if (status === 422) {
    const fieldErrors = getAuthErrorFieldErrors(error);
    // `userEmailAlreadyInUse` is what UsersService.create throws on register.
    if (
      fieldErrors?.email === 'userEmailAlreadyInUse' ||
      fieldErrors?.email === 'alreadyExists' ||
      fieldErrors?.email === 'emailAlreadyExists'
    ) {
      return 'emailAlreadyExists';
    }
    if (
      fieldErrors?.username === 'usernameAlreadyExists' ||
      fieldErrors?.username === 'alreadyExists'
    ) {
      return 'usernameAlreadyExists';
    }
  }
  return 'requestFailed';
}

export function classifyMagicLinkVerifyError(
  error: unknown,
): MagicLinkVerifyErrorKey {
  const status = getAuthErrorStatus(error);
  if (status !== 422) {
    return 'unknownError';
  }

  const fieldErrors = getAuthErrorFieldErrors(error);
  if (fieldErrors?.code === 'invalid') {
    return 'invalidCode';
  }
  if (fieldErrors?.code === 'tooManyAttempts') {
    return 'tooManyAttempts';
  }
  if (fieldErrors?.code === 'expired') {
    return 'expiredCode';
  }
  if (fieldErrors?.code === 'notFound') {
    return 'codeNotFound';
  }
  if (fieldErrors?.email === 'notFound') {
    return 'emailNotFound';
  }
  if (fieldErrors?.email === 'userNotActive') {
    return 'userNotActive';
  }
  return 'verificationFailed';
}
